/**
 * Rozrobený import z TopSolidu — koncept na serveri (KOLO 117).
 *
 * Sprievodca importu bol do tohto kola úplne v pamäti prehliadača:
 * klik vedľa dialógu zmazal aj hodinu priraďovania. Koncept mu dáva
 * miesto na serveri, takže prežije zatvorenie okna, vypnutie appky aj
 * presun na iný počítač.
 *
 * Súbor sa **sťahuje späť ako `File`** a posiela do `/analyza` presne
 * ako pri prvom nahratí. Vďaka tomu zostáva analýza bezstavová a
 * neexistuje druhá cesta k tomu istému súboru, ktorá by sa časom
 * rozišla s prvou.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { DataResponse } from "@/api/schemas";

/** Krok sprievodcu, na ktorom si prestal. */
export type KonceptKrok = "dielce" | "materialy" | "kovania";

export interface Koncept {
  id: string;
  autor: string;
  nazov_suboru: string;
  velkost_b: number;
  cislo_zakazky: string;
  nazov: string;
  krok: KonceptKrok;
  /** Snapshot sprievodcu (`stavy`, `delenie`, `zaradenie`, …) ako JSON. */
  stav_json: string;
  created_at: string;
  updated_at: string;
  /** Kedy prišiel súbor (KOLO 216) — opravený export z Tandem Zostáv ho
   *  vymení v tom istom koncepte a otvorený sprievodca ho načíta znova. */
  subor_at?: string | null;
  /** `rucne` / `zostavy` — okno „Nový import" len pre `zostavy` (KOLO 128).
   *  `regal` = diely Tandemu z konfigurátora regálu CRC pre rozpočtára (KOLO 232). */
  zdroj?: "rucne" | "zostavy" | "regal";
  /** Koncept pre rolu (KOLO 232) — `rozpoctar`, kým ho rozpočtár neprevezme. */
  pre_rolu?: string | null;
  /** `update` = aktualizácia existujúcej zákazky z opraveného exportu. */
  rezim?: "novy" | "update";
  zakazka_id?: string | null;
  /** Ktorý výpočet sa aktualizuje; z Tandem Zostáv prichádza prázdny. */
  varianta_id?: string | null;
  /** Súhrn pre okno nového importu (len z Tandem Zostáv). */
  dielcov?: number | null;
  zostav?: number | null;
  /** Kedy user okno s novým importom videl — `null` = ešte nie. */
  videny_at?: string | null;
  /** Mená zostáv z Tandem Zostáv `{urceni: jmeno}` (KOLO 146) — sprievodca
   *  ich pošle späť pri potvrdení aj aktualizácii. */
  zostavy_mena?: Record<string, string> | null;
  /** Určení zostáv vybraných v Tandem Zákazke (KOLO 175) — `null` = celý
   *  kusovník. Sprievodca ich pošle späť pri analýze, potvrdení aj update. */
  zostavy_vybrane?: string[] | null;
  /** Koľko priamych podzostáv program poslal („vybrané zostavy: 2 z 39"). */
  zostav_v_zakazke?: number | null;
  /** Rozpočet bez čísla z Tandem Zákazky (KOLO 200): `neskor` = výpočet V,
   *  `z` = malá zákazka bez AA (číslo Z je v `cislo_zakazky`). */
  bez_cisla_typ?: "neskor" | "z" | null;
  /** Normalizovaná cesta výkresu — sprievodca ju pošle pri importe. */
  vykres_cesta?: string | null;
  /** Časť zákazky z dovetku Určení (KOLO 203) — názov novej časti. */
  cast?: string | null;
  /** Návrh okna pri existujúcej zákazke: `true` = pridať ako novú časť,
   *  `false` = aktualizovať (`varianta_id`, ak sa časť spárovala). */
  cast_nova?: boolean | null;
  /** Výpočet z toho istého výkresu (KOLO 208) — okno nového importu výroby
   *  ho predvolí ako „K výpočtu". */
  navrh_vypoctu?: { id: string; cislo_zakazky: string; nazov?: string | null } | null;
  /** Číslo konceptu zmizlo spojením výrob (KOLO 209) — zákazka, kde je teraz. */
  spojena_s?: { id: string; cislo_zakazky: string; nazov?: string | null } | null;
}

/** Čo sa posiela pri autosave — súbor sa neposiela, ten sa nemení. */
export interface KonceptStav {
  cislo_zakazky: string;
  nazov: string;
  krok: KonceptKrok;
  stav_json: string;
  /** Výpočet pri aktualizácii — posiela sa len v režime update. */
  varianta_id?: string | null;
  /** Voľba okna pri existujúcej zákazke (KOLO 203) — nová časť a jej názov. */
  cast_nova?: boolean | null;
  cast?: string | null;
}

/**
 * Otvorená appka sa pýta na nové importy z Tandem Zostáv každých 15 s
 * a pri návrate do okna (KOLO 215) — okno „Nový import" sa tak ukáže,
 * aj keď program otvoril odkaz inde. Do KOLA 215 sa zoznam načítal raz.
 */
export const OBNOVA_KONCEPTOV_MS = 15_000;

export function useKoncepty(): UseQueryResult<DataResponse<Koncept[]>> {
  return useQuery({
    queryKey: queryKeys.topsolidKoncepty.all,
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Koncept[]>>(
        "/import/topsolid/koncepty",
      );
      return data;
    },
    refetchInterval: OBNOVA_KONCEPTOV_MS,
    refetchOnWindowFocus: true,
  });
}

export function useZalozKoncept(): UseMutationResult<
  DataResponse<Koncept>,
  Error,
  KonceptStav & {
    file: File;
    /** Výber z Tandem Zákazky prejde do nového konceptu (KOLO 175) */
    zostavyVybrane?: string[] | null;
    zostavVZakazke?: number | null;
    /** Zákazka novej časti (KOLO 203) — PUT ju nepozná, ide len sem. */
    zakazkaCasti?: string | null;
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, zostavyVybrane, zostavVZakazke, zakazkaCasti, ...stav }) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("stav_json", stav.stav_json);
      fd.append("cislo_zakazky", stav.cislo_zakazky);
      fd.append("nazov", stav.nazov);
      fd.append("krok", stav.krok);
      // Koncept aktualizácie si režim pamätá (KOLO 128) — bez toho sa
      // minimalizovaný update po obnovení tváril ako nový import (409).
      if (stav.varianta_id) fd.append("varianta_id", stav.varianta_id);
      // Nová časť existujúcej zákazky (KOLO 203) — bez toho by sa opravený
      // export časti tváril ako nová zákazka
      if (stav.cast_nova && zakazkaCasti) {
        fd.append("zakazka_id", zakazkaCasti);
        fd.append("cast_nova", "true");
        if (stav.cast) fd.append("cast", stav.cast);
      }
      if (zostavyVybrane && zostavyVybrane.length > 0) {
        fd.append("zostavy_vybrane_json", JSON.stringify(zostavyVybrane));
        if (zostavVZakazke != null) fd.append("zostav_v_zakazke", String(zostavVZakazke));
      }
      const { data } = await api.post<DataResponse<Koncept>>(
        "/import/topsolid/koncept",
        fd,
      );
      return data;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all }),
  });
}

export function useUlozKoncept(): UseMutationResult<
  DataResponse<Koncept>,
  Error,
  KonceptStav & { id: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...stav }) => {
      const { data } = await api.put<DataResponse<Koncept>>(
        `/import/topsolid/koncept/${id}`,
        stav,
      );
      return data;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all }),
  });
}

/**
 * User videl okno „Nový import z Tandem Zostáv" (Otvoriť / Neskôr).
 * Okno sa potom samo neukáže; koncept ostáva v pruhu.
 */
export function useOznacVideny(): UseMutationResult<
  DataResponse<Koncept>,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DataResponse<Koncept>>(
        `/import/topsolid/koncept/${id}/videny`,
      );
      return data;
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all }),
  });
}

export function useZmazKoncept(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/import/topsolid/koncept/${id}`);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.topsolidKoncepty.all }),
  });
}

/**
 * Stiahne uložený export späť ako `File`.
 *
 * Nie je to hook — volá sa až na klik „Pokračovať". Sťahovať stovky kB
 * pri každom načítaní stránky len preto, že niekde leží rozrobený
 * import, by bola daň za nič.
 */
export async function stiahniKonceptSubor(k: Koncept): Promise<File> {
  const { data } = await api.get<Blob>(
    `/import/topsolid/koncept/${k.id}/subor`,
    { responseType: "blob" },
  );
  return new File([data], k.nazov_suboru);
}
